import PageBackTitle from "../Component/PageBackTitle";
import React, { useEffect, useRef, useState } from "react";
import "./User.css";
import "./Button.css";
import "../forms/UserForm.css";
import "../BarrelManagement/PurchaseIndents/purchaseIndents.css";
import "../SafetyObservation/safetyObservation.css";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlinePlus } from "react-icons/hi";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import { MdExpandMore, MdOutlineModeEditOutline } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import API_URL from "../../src/Config";
import Pagination from "../Component/Pagination";
import {
  formatDateDisplay,
  parseApiError,
} from "../SafetyObservation/observationUtils";

const INITIAL_FILTERS = {
  start_date: "",
  end_date: "",
};

function SafetyMeetingsList({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [meetingToDelete, setMeetingToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [paginationKey, setPaginationKey] = useState(0);
  const popupRef = useRef(null);
  const hasActiveFilters = Boolean(
    filters.start_date.trim() || filters.end_date.trim() || searchInput.trim(),
  );

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const fetchMeetings = async (currentPage, currentItemsPerPage, activeFilters) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const params = {
        page: currentPage,
        per_page: currentItemsPerPage,
      };

      if (activeFilters.start_date) {
        params.start_date = activeFilters.start_date;
      }
      if (activeFilters.end_date) {
        params.end_date = activeFilters.end_date;
      }
      if (searchInput.trim()) {
        params.search = searchInput.trim();
      }

      const response = await axios.get(`${API_URL}/api/safety/meetings`, {
        headers: {
          Authorization: authKey,
        },
        params,
      });

      setMeetings(response.data.items || []);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to fetch meetings"), "error");
      setMeetings([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings(page, itemsPerPage, filters);
  }, [page, itemsPerPage, filters, searchInput]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setSelectedRowIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePaginationChange = (currentPage, nextItemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(nextItemsPerPage);
  };

  const handleRefresh = () => {
    if (!hasActiveFilters) {
      fetchMeetings(page, itemsPerPage, filters);
      return;
    }

    setFilters(INITIAL_FILTERS);
    setSearchInput("");
    setPage(1);
    setPaginationKey((prevState) => prevState + 1);
  };

  const handleFilterChange = (fieldName, value) => {
    setFilters((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
    setPage(1);
    setPaginationKey((prevState) => prevState + 1);
  };

  const handleButtonClick = (index, event) => {
    event.stopPropagation();
    setSelectedRowIndex((prevState) => (prevState === index ? null : index));
  };

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
    setPage(1);
    setPaginationKey((prevState) => prevState + 1);
  };

  const handleDeleteMeeting = async () => {
    if (!meetingToDelete) return;

    try {
      setIsDeleting(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.delete(
        `${API_URL}/api/safety/meetings/${meetingToDelete.id}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      showSnackbar(response.data?.message || "Meeting deleted successfully");
      const nextPage = meetings.length === 1 && page > 1 ? page - 1 : page;
      setMeetingToDelete(null);

      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        fetchMeetings(nextPage, itemsPerPage, filters);
      }
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to delete meeting"), "error");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Meetings List</PageBackTitle>
              <div className="data-list-heading-content-right-side observation-header-actions">
                <div className="observation-header-filters">
                  <div className="observation-filter-date">
                    <input
                      type="date"
                      title="Start date"
                      value={filters.start_date}
                      onChange={(event) =>
                        handleFilterChange("start_date", event.target.value)
                      }
                    />
                  </div>

                  <div className="observation-filter-date">
                    <input
                      type="date"
                      title="End date"
                      value={filters.end_date}
                      min={filters.start_date || undefined}
                      onChange={(event) =>
                        handleFilterChange("end_date", event.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    className="search-bar"
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>

                <Link to="/safety-meetings-form" className="data-list-new">
                  <Tooltip title="New Meeting" arrow>
                    <button className="new-button">
                      <HiOutlinePlus className="new-plus-icon" /> New
                    </button>
                  </Tooltip>
                </Link>

                <Tooltip title="Refresh List" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          <div className="header-separator"></div>

          <div className="data-list-body">
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : meetings.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. NO.</th>
                        <th>TITLE</th>
                        <th>DATE</th>
                        <th>LOCATION</th>
                        <th>CREATED BY</th>
                        <th className="center-alignment">ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {meetings.map((meeting, index) => (
                        <tr key={meeting.id} className="hoverable-row">
                          <td className="center-alignment">
                            {(page - 1) * itemsPerPage + index + 1}
                          </td>
                          <td>{meeting.title || "NA"}</td>
                          <td>{formatDateDisplay(meeting.date)}</td>
                          <td>{meeting.location || "NA"}</td>
                          <td>{meeting.created_by_name || "NA"}</td>
                          <td className="center-alignment">
                            <div className="observation-action-buttons">
                              <button
                                className="button-bg"
                                onClick={() =>
                                  navigate(`/safety-meetings-view/${meeting.id}`)
                                }
                              >
                                View
                              </button>
                              {/* <button
                                className="observation-icon-button observation-icon-button-danger"
                                onClick={() => setMeetingToDelete(meeting)}
                                type="button"
                              >
                                <FiTrash2 />
                              </button> */}
                              <button
                                className="edit-data-button"
                                onClick={(event) =>
                                  handleButtonClick(index, event)
                                }
                              >
                                <div className="edit-button">
                                  <MdExpandMore />
                                </div>
                              </button>
                            </div>
                            {selectedRowIndex === index && (
                              <div
                                className="edit-popup"
                                style={{ top: "58%", right: "4%" }}
                                ref={popupRef}
                              >
                                <div className="edit-popup-contant">
                                  <div className="edit-popup-button">
                                    <Link
                                      to={`/edit-safety-meeting/${meeting.id}`}
                                      className="inquiry-new"
                                    >
                                      <button className="edit-button-inquiry">
                                        <MdOutlineModeEditOutline /> Edit
                                      </button>
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">No Meetings Found</div>
                  </div>
                )}
              </div>

              {totalItems > 0 && (
                <Pagination
                  key={paginationKey}
                  showPerPage={itemsPerPage}
                  total={totalItems}
                  onPaginationChange={handlePaginationChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {meetingToDelete && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>
                    Are you sure you want to delete meeting{" "}
                    <strong>{meetingToDelete.title || "this item"}</strong>?
                  </p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleDeleteMeeting}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={() => !isDeleting && setMeetingToDelete(null)}
                    disabled={isDeleting}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default SafetyMeetingsList;
